import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { LinkIcon, TrashIcon, ShieldCheckIcon } from '@heroicons/react/24/outline';


const API_BASE_URL = import.meta.env.VITE_API_BASE_URL || `\${API_BASE_URL}`;

interface ConnectedApp {
  id: string;
  name: string;
  icon?: string;
  description: string;
  scopes: string[];
  connectedAt: string;
  lastUsed?: string;
}

const ConnectedAppsPage: React.FC = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [apps, setApps] = useState<ConnectedApp[]>([]);
  const [revokingId, setRevokingId] = useState<string | null>(null);

  useEffect(() => {
    fetchConnectedApps();
  }, []);

  const fetchConnectedApps = async () => {
    const token = localStorage.getItem('token');
    if (!token) return;

    try {
      const res = await fetch(`\${API_BASE_URL}/api/settings/connected-apps`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (res.ok) {
        const data = await res.json();
        setApps(data.apps || []);
      }
    } catch (error) {
      console.error('Failed to load connected apps:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleRevoke = async (appId: string) => {
    if (!confirm('Are you sure you want to revoke access for this app?')) {
      return;
    }

    setRevokingId(appId);
    const token = localStorage.getItem('token');
    if (!token) return;

    try {
      const res = await fetch(`${API_BASE_URL}/api/settings/connected-apps/${appId}`, {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${token}` },
      });
      if (res.ok) {
        await fetchConnectedApps();
      }
    } catch (error) {
      console.error('Failed to revoke app:', error);
    } finally {
      setRevokingId(null);
    }
  };

  if (loading) {
    return (
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-indigo-600 mx-auto"></div>
      </div>
    );
  }

  return (
    <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
      <div className="mb-6">
        <button
          onClick={() => navigate('/dashboard/settings')}
          className="text-sm text-indigo-600 hover:text-indigo-500 mb-4"
        >
          ← Back to Settings
        </button>
        <h1 className="text-3xl font-bold text-gray-900">Connected Apps</h1>
        <p className="mt-2 text-sm text-gray-600">
          Manage third-party applications with access to your account
        </p>
      </div>

      {apps.length === 0 ? (
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-12 text-center">
          <LinkIcon className="w-12 h-12 text-gray-400 mx-auto mb-4" />
          <h3 className="text-lg font-medium text-gray-900 mb-2">No connected apps</h3>
          <p className="text-sm text-gray-600">
            You haven't authorized any third-party applications yet
          </p>
        </div>
      ) : (
        <div className="space-y-4">
          {apps.map((app) => (
            <div
              key={app.id}
              className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 hover:shadow-md transition-shadow"
            >
              <div className="flex items-start justify-between">
                <div className="flex items-start space-x-4">
                  {app.icon ? (
                    <img src={app.icon} alt={app.name} className="w-12 h-12 rounded-lg" />
                  ) : (
                    <div className="w-12 h-12 rounded-lg bg-indigo-100 flex items-center justify-center">
                      <LinkIcon className="w-6 h-6 text-indigo-600" />
                    </div>
                  )}
                  <div className="flex-1">
                    <h3 className="text-lg font-medium text-gray-900">{app.name}</h3>
                    <p className="text-sm text-gray-600 mt-1">{app.description}</p>
                    <div className="mt-3">
                      <span className="text-xs font-medium text-gray-500 uppercase">
                        Permissions:
                      </span>
                      <div className="flex flex-wrap gap-2 mt-2">
                        {app.scopes.map((scope) => (
                          <span
                            key={scope}
                            className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-indigo-100 text-indigo-800"
                          >
                            {scope}
                          </span>
                        ))}
                      </div>
                    </div>
                    <div className="mt-3 text-xs text-gray-500">
                      <div>
                        Connected: {new Date(app.connectedAt).toLocaleDateString()}
                      </div>
                      {app.lastUsed && (
                        <div>Last used: {new Date(app.lastUsed).toLocaleDateString()}</div>
                      )}
                    </div>
                  </div>
                </div>
                <button
                  onClick={() => handleRevoke(app.id)}
                  disabled={revokingId === app.id}
                  className="ml-4 p-2 text-red-600 hover:bg-red-50 rounded-md disabled:opacity-50"
                  title="Revoke access"
                >
                  <TrashIcon className="w-5 h-5" />
                </button>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Info Panel */}
      <div className="mt-6 bg-blue-50 border border-blue-200 rounded-lg p-4">
        <div className="flex items-start">
          <ShieldCheckIcon className="w-5 h-5 text-blue-600 mt-0.5 mr-3" />
          <div>
            <h4 className="text-sm font-medium text-blue-900">Security Reminder</h4>
            <p className="mt-1 text-sm text-blue-700">
              Review your connected apps regularly and revoke access for apps you no longer use.
              Revoking access will not delete any data already shared with the app.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ConnectedAppsPage;
